// FSTable.jsx
import React, { useEffect, useState, useRef } from "react";
import ChecklistModal from "../../components/standalonefs/ChecklistModal";
import ReferenceModal from "../../components/standalonefs/ReferenceModal";
import commoncss from "../../css/common.module.css";

/*
  Props:
    - columns: [{ label, field, align, editable, type, optionValue?, minWidth?, width? }]
        minWidth and width are numbers (pixels). e.g. minWidth: 140, width: 200
    - rows: array of row objects (row.value is "Y"|"N"|"NA" or null)
    - onRowsChange(updatedRows) // called on commit/blur
    - onCellChangeImmediate({ _id, key, value }) // NEW: called on keyup (text) or change (checkbox)
    - brandingClass, emptyMessage
    - containerWidth: optional; number (pixels) or string (e.g. "800px" or "100%"). Default "100%".
*/
export default function FSTable({
  columns = [],
  rows = [],
  onRowsChange = () => {},
  onCellChangeImmediate = () => {}, // signature: ({ _id, key, value }) => {}
  brandingClass = "ft-branding",
  emptyMessage = "No data for this category",
  containerWidth = "100%",
  redirectTo = () => {},
  checklistRef = () => {},
  tableTitle=null
}) {
  const [localRows, setLocalRows] = useState(
    Array.isArray(rows) ? [...rows] : []
  );
  const [editing, setEditing] = useState(null); // { rowIdx, field, col }
  const inputRef = useRef(null);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [viewReferenceModal, setViewReferenceModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [refrenceModalData, setReferenceModalData] = useState(null);

  useEffect(() => {
    setLocalRows(Array.isArray(rows) ? [...rows] : []);
    setEditing(null);
  }, [rows]);

  useEffect(() => {
    if (editing && inputRef.current) {
      try {
        inputRef.current.focus();
        inputRef.current.select?.();
      } catch (e) {}
    }
  }, [editing]);

  const startEdit = (rowIdx, col) => {
    if (!col.editable) return;
    if (col.type === "checkbox") return; // checkboxes handled directly
    if ((localRows[rowIdx]?.lineType || "").toLowerCase() === "header") return;
    setEditing({ rowIdx, field: col.field, col });
  };

  const commitValue = (valueStr) => {
    if (!editing) return;
    const { rowIdx, field, col } = editing;
    const updated = [...localRows];
    let newVal = valueStr;

    if (col.type === "number") {
      newVal = valueStr === "" ? null : Number(valueStr);
      if (isNaN(newVal)) newVal = null;
    } else if (col.type === "array") {
      newVal = String(valueStr)
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    }

    updated[rowIdx] = { ...updated[rowIdx], [field]: newVal };
    setLocalRows(updated);
    setEditing(null);

    try {
      onRowsChange(updated);
    } catch (e) {
      console.error("onRowsChange error", e);
    }
  };

  // Emit single-object immediate change: { _id, key, value }
  const emitImmediateChange = (rowIdx, key, value) => {
    const row = localRows[rowIdx] ?? {};
    const id = row._id ?? rowIdx;
    try {
      onCellChangeImmediate({ _id: id, key, value });
    } catch (e) {
      console.error("onCellChangeImmediate error", e);
    }
  };

  // Handles single-value checkbox option columns (field: "value", optionValue: "Y"/"N"/"NA")
  const toggleSingleValueCheckbox = (rowIdx, col, checked) => {
    if (!col.optionValue) {
      console.warn("[FSTable] checkbox column missing optionValue", col);
      return;
    }
    const updated = [...localRows];
    const row = { ...updated[rowIdx] };

    if (checked) {
      row[col.field] = col.optionValue;
    } else {
      row[col.field] = null;
    }

    updated[rowIdx] = row;
    setLocalRows(updated);

    // immediate emit single-object diff
    emitImmediateChange(rowIdx, col.field, row[col.field]);

    try {
      onRowsChange(updated);
    } catch (e) {
      console.error("onRowsChange error", e);
    }
  };

  const cancel = () => setEditing(null);

  const displayValue = (row, field, type) => {
    const v = row[field];
    if (v == null || v === "") return "";
    if (Array.isArray(v)) return v.join(", ");
    if (type === "number" && typeof v === "number") return v.toLocaleString();
    if (type === "checkbox") return "";
    return String(v);
  };

  // compute total minimum width from columns (use default 120 if not provided)
  const totalMinWidthPx = columns.reduce(
    (sum, c) => sum + (c.minWidth ?? 120),
    0
  );

  // normalize containerWidth prop to CSS value
  const normalizedContainerWidth =
    typeof containerWidth === "number"
      ? `${containerWidth}px`
      : containerWidth || "100%";

  const handleChecklistModal = (data) => {
    setModalData([]);
    const modalDataObj = {
      header: data.reference,
      libRef: data.libraryReference,
    };
    setModalData(modalDataObj);
    setShowChecklistModal(true);
  };

    const handleReferenceModal = (data) => {
    setReferenceModalData(data);
    console.log("handleReferenceModal data",data);
    
    setViewReferenceModal(true);
  };
  return (
    <div
      style={{
        width: normalizedContainerWidth,
        overflowX: "auto",
        marginTop: "20px",
      }}
    >
      <style>{`
        .ft-branding th { background-color: rgb(239,247,240) !important; color: #33584e !important; }
        .ft-table { border-collapse: collapse; width: 100%; font-family: inherit; table-layout: auto; }
        .ft-table th, .ft-table td { border: 1px solid #e5e7eb; padding: 10px 12px; font-size: 14px; vertical-align: middle; position: relative; white-space: nowrap; }
        .ft-align-left { text-align: left; } .ft-align-center { text-align: center; } .ft-align-right { text-align: right; }
        .ft-edit-input { position: absolute; inset: 0; width: 100%; height: 100%; box-sizing: border-box; padding: 10px 12px; margin: 0; border: none; background: transparent; outline: none; font-size: 14px; font-family: inherit; color: inherit; }
        .ft-input-left { text-align: left; } .ft-input-center { text-align: center; } .ft-input-right { text-align: right; }
        .ft-editing::after { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: 1px; background: rgba(51,88,78,0.12); pointer-events: none; }
        input[type=number]::-webkit-inner-spin-button, input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
        .ft-checkbox { display: inline-block; transform: scale(1.0); margin: 0; vertical-align: middle; cursor: pointer; }
      `}</style>
      {tableTitle && (<span className={`${commoncss.fs22} ${commoncss.fw500}`}>{tableTitle}</span>)}
      <table
        className={`ft-table ${brandingClass}`}
        // Ensure the table won't shrink below the total minimum width (causes wrapper to show horizontal scroll when needed)
        style={{ minWidth: `${totalMinWidthPx}px` }}
      >
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th
                key={i}
                style={{
                  minWidth: c.minWidth ? `${c.minWidth}px` : undefined,
                  maxWidth: c.maxWidth ? `${c.maxWidth}px` : undefined,
                  width: c.width ? `${c.width}px` : undefined,
                }}
                className={
                  c.align === "right"
                    ? "ft-align-right"
                    : c.align === "center"
                    ? "ft-align-center"
                    : "ft-align-left"
                }
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {!localRows || localRows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{ textAlign: "center", padding: 20 }}
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            localRows.map((row, rIdx) => {
              const rowKey = row._id ?? rIdx;

              if ((row.lineType || "").toLowerCase() === "header") {
                return (
                  <tr key={rowKey}>
                    <td
                      colSpan={columns.length}
                      style={{
                        background: "rgb(239,247,240)",
                        color: "#33584e",
                        fontWeight: 600,
                        textAlign: "left",
                      }}
                    >
                      {row.particular}
                    </td>
                  </tr>
                );
              }

              return (
                <tr key={rowKey}>
                  {columns.map((col, cIdx) => {
                    const isEditing =
                      editing &&
                      editing.rowIdx === rIdx &&
                      editing.field === col.field;
                    const inputAlignClass =
                      col.align === "right"
                        ? "ft-input-right"
                        : col.align === "center"
                        ? "ft-input-center"
                        : "ft-input-left";
                    const cellText = displayValue(row, col.field, col.type);

                    const handleCellClick = () => {
                      if (col.type === "checkbox") return;
                      startEdit(rIdx, col);
                    };

                    return (
                      <td
                        key={cIdx}
                        style={{
                          minWidth: col.minWidth
                            ? `${col.minWidth}px`
                            : undefined,
                          width: col.width ? `${col.width}px` : undefined,
                        }}
                        className={`${
                          col.align === "right"
                            ? "ft-align-right"
                            : col.align === "center"
                            ? "ft-align-center"
                            : "ft-align-left"
                        } ${isEditing ? "ft-editing" : ""}`}
                        onClick={handleCellClick}
                        onDoubleClick={handleCellClick}
                      >
                        {col.type === "popup" && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent:
                                col.align === "right"
                                  ? "flex-end"
                                  : col.align === "center"
                                  ? "center"
                                  : "flex-start",
                            }}
                          >
                            <span
                              onClick={() => {
                                handleChecklistModal(row);
                                console.log(row);
                              }}
                            >
                              {row[col.field]}
                            </span>
                          </div>
                        )}
                        {col.type === "checklistref" && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent:
                                col.align === "right"
                                  ? "flex-end"
                                  : col.align === "center"
                                  ? "center"
                                  : "flex-start",
                            }}
                          >
                            {Array.isArray(row[col.field])
                              ? row[col.field].map((item, index) => (
                                  <span key={index}>
                                    <span
                                      onClick={() => {
                                        handleReferenceModal(item)
                                        console.log("ref item", item);
                                      }}
                                      style={{
                                        cursor: "pointer",
                                        marginRight: "8px",
                                        textDecoration: "underline",
                                      }}
                                    >
                                      {item}
                                    </span>
                                    {index < row[col.field].length - 1 && ", "}
                                  </span>
                                ))
                              : row[col.field] ?? ""}
                          </div>
                        )}
                        {col.type === "redirect" && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent:
                                col.align === "right"
                                  ? "flex-end"
                                  : col.align === "center"
                                  ? "center"
                                  : "flex-start",
                            }}
                          >
                            <span
                              onClick={() => {
                                redirectTo(row[col.field]);
                              }}
                            >
                              {row[col.field]}
                            </span>
                          </div>
                        )}
                        {col.type === "checkbox" ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent:
                                col.align === "right"
                                  ? "flex-end"
                                  : col.align === "center"
                                  ? "center"
                                  : "flex-start",
                            }}
                          >
                            <input
                              name={`chk_${rowKey}_${col.field}_${col.optionValue}`}
                              className="ft-checkbox"
                              type="checkbox"
                              checked={row[col.field] === col.optionValue}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                toggleSingleValueCheckbox(rIdx, col, checked);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`${col.label} checkbox row ${rIdx}`}
                            />
                          </div>
                        ) : (
                          <>
                            {col.type !== "redirect" &&
                              col.type !== "popup" &&
                              col.type !== "checklistref" && (
                                <div
                                  style={{
                                    visibility: isEditing
                                      ? "hidden"
                                      : "visible",
                                  }}
                                >
                                  {cellText}
                                </div>
                              )}

                            {isEditing &&
                              col.type !== "redirect" &&
                              col.type !== "popup" &&
                              col.type !== "checklistref" && (
                                <input
                                  ref={inputRef}
                                  className={`ft-edit-input ${inputAlignClass}`}
                                  type={
                                    col.type === "number" ? "number" : "text"
                                  }
                                  defaultValue={
                                    col.type === "array" &&
                                    Array.isArray(row[col.field])
                                      ? row[col.field].join(", ")
                                      : row[col.field] ?? ""
                                  }
                                  onBlur={(e) => commitValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter")
                                      commitValue(e.target.value);
                                    if (e.key === "Escape") cancel();
                                  }}
                                  onKeyUp={(e) => {
                                    const raw = e.target.value;
                                    // compute outgoing according to type
                                    let outgoing = raw;
                                    if (col.type === "number") {
                                      const n = raw === "" ? null : Number(raw);
                                      outgoing = isNaN(n) ? null : n;
                                    } else if (col.type === "array") {
                                      outgoing = String(raw)
                                        .split(",")
                                        .map((v) => v.trim())
                                        .filter(Boolean);
                                    }
                                    // update localRows so UI is responsive while typing
                                    const updated = [...localRows];
                                    updated[rIdx] = {
                                      ...updated[rIdx],
                                      [col.field]: outgoing,
                                    };
                                    setLocalRows(updated);
                                    // emit single-object immediate diff
                                    emitImmediateChange(
                                      rIdx,
                                      col.field,
                                      outgoing
                                    );
                                  }}
                                />
                              )}
                          </>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      {showChecklistModal && modalData && (
        <ChecklistModal
          setShowChecklistModal={setShowChecklistModal}
          modalData={modalData}
        />
      )}
      {viewReferenceModal && refrenceModalData && (
        <ReferenceModal
          setViewReferenceModal={setViewReferenceModal}
          modalData={refrenceModalData}
        />
      )}
    </div>
  );
}
